import type { PageEntry, SearchParams } from './types'

/** Limits of the API; beyond them it answers 400 INVALID_PARAMETER. */
export const MAX_PAGE = 1000
export const MAX_QUERY_LENGTH = 200

/** The search state of a URL, clamped to the API limits (a deep link may carry anything). */
export function readSearchParams(search: string): SearchParams {
    const params = new URLSearchParams(search)
    const rawPage = Number.parseInt(params.get('page') ?? '', 10)

    return {
        q: clampQuery(params.get('q') ?? ''),
        page: Number.isInteger(rawPage) ? clampPage(rawPage) : 1,
    }
}

/** Trimmed and cut to MAX_QUERY_LENGTH characters (code points, like the API). */
export function clampQuery(q: string): string {
    const chars = Array.from(q.trim())

    return chars.length > MAX_QUERY_LENGTH ? chars.slice(0, MAX_QUERY_LENGTH).join('').trim() : chars.join('')
}

export function clampPage(page: number): number {
    return Math.min(MAX_PAGE, Math.max(1, Math.floor(page)))
}

/** Builds the query string for a search state, keeping unrelated parameters. Empty string for the bare path. */
export function buildSearchQuery(currentSearch: string, params: SearchParams): string {
    const query = new URLSearchParams(currentSearch)
    const q = params.q.trim()

    if (q) {
        query.set('q', q)
    } else {
        query.delete('q')
    }

    if (params.page > 1) {
        query.set('page', String(params.page))
    } else {
        query.delete('page')
    }

    const result = query.toString()

    return result ? `?${result}` : ''
}

/** The link as shown below a teaser: decoded, without scheme and trailing slash. */
export function displayUrl(link: string): string {
    let decoded = link

    try {
        decoded = decodeURI(link)
    } catch {
        // keep the raw link when it is not valid percent encoding
    }

    return decoded.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

/** Page numbers to show: first, last and `radius` pages around the current one, gaps as 'gap'. */
export function pageWindow(page: number, pageCount: number, radius = 2): PageEntry[] {
    if (pageCount < 1) {
        return []
    }

    const pages = new Set<number>([1, pageCount])

    for (let candidate = page - radius; candidate <= page + radius; candidate++) {
        if (candidate >= 1 && candidate <= pageCount) {
            pages.add(candidate)
        }
    }

    const sorted = [...pages].sort((a, b) => a - b)
    const entries: PageEntry[] = []

    sorted.forEach((current, index) => {
        const previous = sorted[index - 1]

        if (index > 0 && previous !== undefined && current - previous > 1) {
            entries.push('gap')
        }

        entries.push(current)
    })

    return entries
}

/** The link for `href`: http(s), protocol-relative and relative URLs; undefined for other schemes such as `javascript:`. */
export function safeHref(link: string): string | undefined {
    const scheme = /^\s*([a-z][a-z0-9+.-]*):/i.exec(link)?.[1]?.toLowerCase()

    return scheme === undefined || scheme === 'http' || scheme === 'https' ? link : undefined
}
